import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import { Theme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import {
  APIActions,
  BlankState,
  Box,
  ContentTemplate,
  CustomDialog,
  Divider,
  Grid,
  HtmlTrans,
  KeyValue,
  MUIDataTable,
  MUIDataTableOptions,
  SectionHeader,
  TableActionTypes,
  Typography,
  useDataTable,
} from 'web-library';
import { candidateService } from '../../../../../api';
import { useAccessArrangementReviewColumns } from '../../../../../pages/accessArrangement/ReviewApplication/logic/useAccessArrangementReviewColumns';
import Declaration from '../../../../../components/Declaration';
import NavigationButtons from '../../../../../components/NavigationButtons';
import DraftTimeStamp from '../../../../../components/DraftTimeStamp';
import RouteKey from '../../../../../routes/key';
import {
  AccessArrangementGetReviewAmendment,
  AccessArrangementGetReviewDetails,
  AccessArrangementRequestDetailModel,
  AccessArrangementReviewBase,
  MedicalCondition,
} from '../../../../../api/candidate/access-arrangement/model';
import { useGetUserDetails } from '../../../../../api-refactored/user/useGetUserDetails';

const {
  root,
  submitted: submittedRoute,
  individualReport,
  coordinatorRoot,
  coordinatorSubmitted,
  coordinatorIndividualReport,
} = RouteKey['access-arrangement'].child;
const { accessArrangement } = RouteKey.child;

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  emptyReserve: {
    height: theme.spacing(40),
  },
  emptyContainer: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 77,
    paddingBottom: 77,
  },
  spaceBottom: {
    marginBottom: theme.spacing(2),
  },
  subheader: {
    marginBottom: theme.spacing(4),
  },
  keyValueContainer: {
    marginTop: theme.spacing(2),
  },
  personalDetailsContainer: {
    marginBottom: theme.spacing(4),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(4),
    marginButton: theme.spacing(3),
  },
  examLevel: {
    color: theme.color.text.textHeader,
  },
  requestTable: {
    width: '100%',
    marginBottom: theme.spacing(6),
  },
  dropped: {
    marginLeft: theme.spacing(1),
    color: theme.color.state.stateError,
  },
  coordinatorDetails: {
    marginBottom: theme.spacing(3),
  },
}));

function instanceOfDraftDetails(
  data: AccessArrangementGetReviewDetails | AccessArrangementGetReviewAmendment
): data is AccessArrangementGetReviewDetails {
  return 'medicalConditionModel' in data;
}

function instanceOfAmendmentDetails(
  data: AccessArrangementGetReviewDetails | AccessArrangementGetReviewAmendment
): data is AccessArrangementGetReviewAmendment {
  return 'medicalConditionAmendmentModel' in data;
}

const getInvalidMedicalCondition = (
  data: AccessArrangementGetReviewDetails | AccessArrangementGetReviewAmendment
) => {
  let medicalCondition: MedicalCondition[] = [];
  if (instanceOfDraftDetails(data)) {
    medicalCondition = data.medicalConditionModel.medicalConditionDropdownModel.medicalConditions;
  } else if (instanceOfAmendmentDetails(data)) {
    medicalCondition =
      data.medicalConditionAmendmentModel.medicalConditionDraftModel.medicalConditionDropdownModel
        .medicalConditions;
  }

  return medicalCondition.filter((x) => x.invalid);
};

const getInvalidRequestType = (data: AccessArrangementReviewBase) =>
  data.accessArrangementRequestDetailModels
    ? data.accessArrangementRequestDetailModels.filter((x) => x.invalid)
    : [];

interface ReviewApplicationContentProps {
  amendment?: boolean;
  data: AccessArrangementGetReviewDetails | AccessArrangementGetReviewAmendment;
  medicalConditionReason: ReactNode;
}

const ReviewApplicationContent = ({
  amendment = false,
  data,
  medicalConditionReason,
}: ReviewApplicationContentProps) => {
  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string>();
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const { classes } = useStyles();
  const history = useHistory();
  const { state, dispatch: tableDispatch } = useDataTable();
  const [invalidMedicalCondition] = useState<MedicalCondition[]>(getInvalidMedicalCondition(data));
  const [invalidRequestTypes] = useState<AccessArrangementRequestDetailModel[]>(
    getInvalidRequestType(data)
  );
  const [confirmationModalState, setConfirmationModalState] = useState<boolean>(false);
  const [allowProceed, setAllowProceed] = useState(false);

  const {
    data: timestampData,
  } = candidateService.accessArrangement.candidate.useGetAppealArrangementDraftDateTime(uuid);

  const role = window.location.pathname.includes('coordinator') ? 'coordinator' : 'candidate';

  const {
    mutate,
    status: submitApiStatus,
  } = candidateService.accessArrangement.candidate.usePutAppealArrangementReviewDetails(
    () => {
      if (role === 'candidate') {
        history.push(`/${accessArrangement}/${root.route}/${submittedRoute.route}/${uuid}`);
      } else {
        history.push(
          `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorSubmitted.route}/${uuid}`
        );
      }
    },
    () => {
      setErrMsg(t('components->>Error.DefaultErrorMsg'));
      setConfirmationModalState(false);
    }
  );

  const columnOptions = useAccessArrangementReviewColumns(state, classes);

  useEffect(() => {
    if (data) {
      const count = data.accessArrangementRequestDetailModels?.length;

      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.accessArrangementRequestDetailModels,
          count,
        },
      } as TableActionTypes);
    }
  }, [data, tableDispatch]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleAgreement = () => {
    setErrMsg(undefined);
    setIsAgreed(!isAgreed);
  };

  const handleSubmitApplication = () => {
    if (!isAgreed) {
      setErrMsg(t('components->>Declaration.Please confirm declaration before you proceed.'));
      return;
    }
    setAllowProceed(true);
    setErrMsg(undefined);
    if (invalidMedicalCondition.length > 0 || invalidRequestTypes.length > 0) {
      setConfirmationModalState(true);
    } else {
      mutate({ uuid });
    }
  };

  const handleBack = () => {
    if (role === 'candidate') {
      history.push(
        `/${accessArrangement}/${root.route}/${individualReport.route}/${uuid}`,
        { amendment: !!amendment }
      );
      return;
    }
    if (role === 'coordinator') {
      history.push(
        `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorIndividualReport.route}/${uuid}`,
        { amendment: !!amendment }
      );
    }
  };

  const tableOptions: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: (
          <>
            <Box className={classes.emptyReserve}>
              <></>
            </Box>
            <Box className={classes.emptyContainer}>
              <BlankState label={t('accessArrangement->>AccessArrangementRequest.Empty')} />
            </Box>
          </>
        ),
      },
    },
    sort: true,
    setRowProps: () => ({ style: { verticalAlign: 'top' } }),
  };

  const {
    data: summaryData,
  } = candidateService.accessArrangement.candidate.useGetAppealArrangementSummaryDetails(
    uuid,
    amendment
  );

  const { data: userData } = useGetUserDetails();

  return (
    <>
      <Prompt
        when={!allowProceed}
        message={t(
          "accessArrangement->>AccessArrangements.ReminderToSubmitPopUp"
        )}
      />
      <ContentTemplate isDividerBottom>
        <Grid className={classes.personalDetailsContainer}>
          <SectionHeader title={t('accessArrangement->>ReviewApplication.Header')} />
          {role === 'coordinator' && (
            <>
              <Box className={classes.coordinatorDetails}>
                <Grid className={classes.keyValueContainer}>
                  <KeyValue
                    id="applicationId"
                    keyText={t('accessArrangement->>ReviewApplication.ApplicationId')}
                    valueText={
                      amendment
                        ? summaryData?.accessArrangementSubmittedDataModel.applicationId
                        : data.applicationId
                    }
                  />
                </Grid>
                <Grid className={classes.keyValueContainer}>
                  <KeyValue
                    id="coordinatorName"
                    keyText={t('accessArrangement->>ReviewApplication.CoordinatorName')}
                    valueText={amendment ? userData?.data.displayName : data.coordinatorName}
                  />
                </Grid>
              </Box>
              <Divider />
            </>
          )}
          {role === 'coordinator' ? (
            <Typography variant="h2" className={classes.subheader}>
              {t('accessArrangement->>ReviewApplication.SubheaderCoordinator1')}
            </Typography>
          ) : (
            <Typography variant="h2" className={classes.subheader}>
              {t('accessArrangement->>ReviewApplication.Subheader1')}
            </Typography>
          )}
          <Grid className={classes.keyValueContainer}>
            <KeyValue
              id="candidateName"
              keyText={t('accessArrangement->>ReviewApplication.Name')}
              valueText={data.accessArrangementCandidateDetailsModel?.statutoryName}
            />
          </Grid>
          <Grid className={classes.keyValueContainer}>
            <KeyValue
              id="nricFin"
              keyText={t('accessArrangement->>ReviewApplication.NRIC')}
              valueText={data.accessArrangementCandidateDetailsModel?.nricFin}
            />
          </Grid>
        </Grid>
      </ContentTemplate>
      {medicalConditionReason}
      <ContentTemplate isDividerBottom continuous type="full">
        <Typography variant="h2" className={classes.subheader}>
          {t('accessArrangement->>ReviewApplication.Subheader3')}
        </Typography>
        <KeyValue
          id="candidateName"
          keyText={t('accessArrangement->>ReviewApplication.Year')}
          valueText={data?.accessArrangementRequestModel?.examYear}
        />
        <KeyValue
          id="candidateName"
          capitalize={false}
          keyText={t('accessArrangement->>ReviewApplication.ExammLevel')}
          valueText={data?.accessArrangementRequestModel?.examLevels.join(', ')}
        />
        <Grid xs={12} className={classes.requestTable}>
          <MUIDataTable
            columns={columnOptions}
            state={state}
            data={state.data}
            options={tableOptions}
            dispatch={tableDispatch}
          />
        </Grid>
      </ContentTemplate>
      <ContentTemplate continuous>
        <Typography variant="h2" className={classes.subheader}>
          {t('accessArrangement->>ReviewApplication.DeclarationSubheader')}
        </Typography>
        <Declaration
          noTitle
          isAgreed={isAgreed}
          handleAgreement={handleAgreement}
          customContent={t('accessArrangement->>ReviewApplication.DeclarationDescription')}
        />
        {errMsg && (
          <Grid xs={12} md={8}>
            <Typography className={classes.errorText} display="inline" variant="body2">
              {errMsg}
            </Typography>
          </Grid>
        )}
        <NavigationButtons
          backLabel={t('accessArrangement->>ReviewApplication.Buttons.Back')}
          procceedLabel={t('accessArrangement->>ReviewApplication.Buttons.Submit')}
          onBackClick={handleBack}
          onProceedClick={handleSubmitApplication}
        />
        {timestampData && <DraftTimeStamp savedAt={timestampData} />}

        <CustomDialog
          dialogSize="md"
          open={confirmationModalState}
          title={t('accessArrangement->>ReviewApplication.ConfirmationModalTitle')}
          message={
            <>
              {t('accessArrangement->>ReviewApplication.FollowingSelectionNotValid')}
              {invalidMedicalCondition.length > 0 && (
                <HtmlTrans
                  i18nKey="accessArrangement->>ReviewApplication.MedicalConditions"
                  values={{
                    name: invalidMedicalCondition?.map((x) => x.name).join(', '),
                  }}
                />
              )}
              {invalidRequestTypes.length > 0 && (
                <HtmlTrans
                  i18nKey="accessArrangement->>ReviewApplication.RequestTypes"
                  values={{
                    name: invalidRequestTypes
                      ?.map((x) => x.accessArrangementType.description)
                      .join(', '),
                  }}
                />
              )}
              {t('accessArrangement->>ReviewApplication.RemoveItemsForSubmission')}
            </>
          }
          action={
            <APIActions
              onClose={() => setConfirmationModalState(false)}
              apiCall={() => mutate({ uuid, clear: true })}
              apiStatus={submitApiStatus}
              cancelString={t('accessArrangement->>ReviewApplication.Buttons.Cancel')}
              apiString={t('accessArrangement->>ReviewApplication.Buttons.Proceed')}
            />
          }
        />
      </ContentTemplate>
    </>
  );
};
export default ReviewApplicationContent;
